import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../common/Modal';
import './ModalForms.css';
import { createClient, getClients } from '../../services/clientService';
import ClientModal from './ClientModal';

const CreateFileModal = ({
  isOpen,
  onClose,
  onConfirm,
  initialClientId,
  clientSelectDisabled = false,
}) => {
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [createClientError, setCreateClientError] = useState('');
  const [clientId, setClientId] = useState('');
  const [dateIn, setDateIn] = useState('');
  const [dateOut, setDateOut] = useState('');
  const [dateError, setDateError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);

  const hasPredefinedClient = useMemo(() => {
    return initialClientId !== undefined && initialClientId !== null && String(initialClientId) !== '';
  }, [initialClientId]);

  useEffect(() => {
    if (!isOpen) return;
    setClientId(initialClientId ? String(initialClientId) : '');
    setDateIn('');
    setDateOut('');
    setDateError('');
    setCreateClientError('');
  }, [isOpen, initialClientId]);

  const loadClients = useCallback(async () => {
    setLoadingClients(true);
    setLoadError('');
    try {
      const data = await getClients();
      const list = Array.isArray(data) ? data : [];
      setClients(list);
      return list;
    } catch (err) {
      console.error(err);
      setLoadError('Error al cargar clientes');
      setClients([]);
      return [];
    } finally {
      setLoadingClients(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadClients().catch(() => {});
  }, [isOpen, loadClients]);

  const sortedClients = useMemo(() => {
    return [...clients].sort((a, b) => {
      const aKey = `${a?.surname || ''} ${a?.name || ''}`.trim().toLowerCase();
      const bKey = `${b?.surname || ''} ${b?.name || ''}`.trim().toLowerCase();
      return aKey.localeCompare(bKey, 'es');
    });
  }, [clients]);

  const selectedClientExists = useMemo(() => {
    if (!clientId) return false;
    return sortedClients.some((c) => String(c.id) === String(clientId));
  }, [sortedClients, clientId]);

  const datesAreValid = useMemo(() => {
    if (!dateIn || !dateOut) return true;
    // YYYY-MM-DD is lexicographically comparable
    return dateOut >= dateIn;
  }, [dateIn, dateOut]);

  const handleCreateNewClient = async (clientData) => {
    setCreateClientError('');
    setCreatingClient(true);
    try {
      const created = await createClient(clientData);
      const createdId = created?.id;
      const refreshed = await loadClients();

      if (createdId !== undefined && createdId !== null) {
        setClientId(String(createdId));
      } else {
        // Fallback: try to find created client in refreshed list
        const match = refreshed.find((c) => String(c.dni) === String(clientData.dni));
        if (match?.id !== undefined && match?.id !== null) setClientId(String(match.id));
      }

      setClientModalOpen(false);
    } catch (err) {
      console.error(err);
      setCreateClientError('No se pudo crear el cliente');
    } finally {
      setCreatingClient(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!clientId || !dateIn || !dateOut) return;
    if (!datesAreValid) {
      setDateError('La fecha de vuelta debe ser mayor o igual que la fecha de salida.');
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm?.({
        clientId: Number(clientId),
        dateIn,
        dateOut,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Nueva ficha de reserva"
      isOpen={isOpen}
      onClose={submitting ? undefined : onClose}
      footer={(
        <div className="modal-footer">
          <div className="footer-actions">
            <button type="button" className="btn secondary" onClick={onClose} disabled={submitting}>
              Cancelar
            </button>
            <button
              type="submit"
              form="create-file-modal-form"
              className="btn"
              disabled={submitting || loadingClients || !clientId || !selectedClientExists || !dateIn || !dateOut || !datesAreValid}
            >
              {submitting ? 'Creando...' : 'Crear'}
            </button>
          </div>
        </div>
      )}
    >
      <form id="create-file-modal-form" className="modal-form" onSubmit={handleSubmit}>
        <div className="form-grid two-columns">
          <label style={{ gridColumn: '1 / -1' }}>
            Cliente
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select
                name="clientId"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                disabled={clientSelectDisabled || loadingClients || submitting}
                required
                style={{ flex: 1 }}
              >
                {!clientSelectDisabled && <option value="">Selecciona</option>}
                {sortedClients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {`${c.surname || ''}, ${c.name || ''}`.trim()}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn"
                onClick={() => setClientModalOpen(true)}
                disabled={hasPredefinedClient || clientSelectDisabled || loadingClients || submitting || creatingClient}
              >
                + Nuevo cliente
              </button>
            </div>
          </label>

          <label>
            Fecha de salida
            <input
              type="date"
              name="dateIn"
              value={dateIn}
              onChange={(e) => {
                setDateIn(e.target.value);
                setDateError('');
              }}
              disabled={loadingClients || submitting}
              required
            />
          </label>

          <label>
            Fecha de regreso
            <input
              type="date"
              name="dateOut"
              value={dateOut}
              onChange={(e) => {
                setDateOut(e.target.value);
                setDateError('');
              }}
              disabled={loadingClients || submitting}
              min={dateIn || undefined}
              required
            />
          </label>
        </div>

        {loadError && <div className="form-error-message" role="alert">{loadError}</div>}
        {createClientError && <div className="form-error-message" role="alert">{createClientError}</div>}
        {!datesAreValid && <div className="form-error-message" role="alert">La fecha de vuelta debe ser mayor o igual que la fecha de salida.</div>}
        {dateError && <div className="form-error-message" role="alert">{dateError}</div>}
        {!loadingClients && clientId && !selectedClientExists && (
          <div className="form-error-message" role="alert">El cliente seleccionado no existe.</div>
        )}

      </form>

      <ClientModal
        isOpen={clientModalOpen}
        onClose={creatingClient ? undefined : () => setClientModalOpen(false)}
        onSave={handleCreateNewClient}
        initialData={null}
      />
    </Modal>
  );
};

export default CreateFileModal;

